/**
 * Every org the signed-in user belongs to in any capacity.
 *
 * Source of truth: `claims.orgs` — the per-org role map written into
 * the user's auth token by `refreshUserClaims` server-side. Mirrors
 * the mobile hook of the same name (`src/features/org/useMyOrganizations.ts`)
 * so the switcher list shows identical content on both surfaces.
 *
 * The legacy approach (`organizations where memberIds array-contains
 * uid`) is wrong: clients aren't in `memberIds`, so a client logging
 * in would see only their own studio in the switcher and never the
 * studios they were invited to as a client. Reading from
 * `claims.orgs` covers both regular members and clients with one
 * source.
 *
 * Stability: per-org listeners are keyed on a stable sorted string of
 * org-ids. They re-create ONLY when the actual set of org-ids
 * changes — NOT when the claims object reference flips (which
 * `useTokenClaims` does on every initial-then-refresh cycle, ~500 ms
 * after sign-in). Without this guard, snapshots get torn down +
 * recreated mid-flight on every claims update.
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';
import { useTokenClaims } from '@/lib/useTokenClaims';

// Human-readable role labels. Mirrors `src/features/org/permissions.ts`
// `ROLE_LABELS` on mobile so the switcher shows identical copy.
const ROLE_LABELS: Record<string, string> = {
  superAdmin: 'Super Admin',
  admin: 'Admin',
  accountant: 'Accountant',
  manager: 'Manager',
  designer: 'Designer',
  siteEngineer: 'Site Engineer',
  supervisor: 'Supervisor',
  viewer: 'Viewer',
  client: 'Client',
};

export type MyOrgRow = {
  id: string;
  name: string;
  ownerId: string;
  /** You created this org (`ownerId === your uid`). */
  isYourStudio: boolean;
  /** Human-readable role in this org. */
  roleLabel: string;
  /** Uploaded studio logo, if any. Falls back to initials. */
  logoUrl: string | null;
};

export type UseMyOrganizationsResult = {
  orgs: MyOrgRow[];
  loading: boolean;
  error: Error | null;
};

export function useMyOrganizations(): UseMyOrganizationsResult {
  const { user } = useAuth();
  const { claims, loading: claimsLoading } = useTokenClaims();
  const [orgs, setOrgs] = useState<MyOrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Stable structural dep — only changes when the SET of org-ids
  // changes. Without this, listeners would tear down + recreate
  // every time `claims.orgs` got a new object reference (every
  // initial-then-refresh cycle from useTokenClaims).
  const orgIds = useMemo(
    () => Object.keys(claims.orgs ?? {}).sort(),
    [claims.orgs],
  );
  const orgIdsKey = orgIds.join(',');

  // Always-current claims map for the snapshot callbacks. Avoids
  // putting `claims.orgs` in the effect deps.
  const claimsOrgsRef = useRef(claims.orgs);
  useEffect(() => {
    claimsOrgsRef.current = claims.orgs;
    // Re-emit with latest role labels for the case where role
    // changed but the org-ids set didn't (e.g. role upgrade).
    setOrgs((prev) =>
      prev.map((row) => {
        // OWNER FIRST: your own studio always labels Super Admin,
        // regardless of a stale/corrupted claims entry.
        const role = row.isYourStudio ? 'superAdmin' : claims.orgs[row.id];
        const nextLabel = role ? ROLE_LABELS[role] ?? 'Member' : 'Member';
        if (nextLabel === row.roleLabel) return row;
        return { ...row, roleLabel: nextLabel };
      }),
    );
  }, [claims.orgs]);

  useEffect(() => {
    if (!user) {
      setOrgs([]);
      setLoading(false);
      setError(null);
      return;
    }
    // Wait for claims to settle before declaring "no orgs". Otherwise
    // the switcher flashes empty for ~500 ms after sign-in while
    // useTokenClaims hydrates from the cached token.
    if (claimsLoading) {
      setLoading(true);
      return;
    }
    if (orgIds.length === 0) {
      setOrgs([]);
      setLoading(false);
      setError(null);
      return;
    }

    const uid = user.uid;
    setLoading(true);
    setError(null);

    // Per-org docs land at different times. Hold them in a Map keyed
    // by orgId so each snapshot delivery overwrites cleanly. Sorting
    // happens on every emit (list is short — typically < 10).
    const byId = new Map<string, MyOrgRow>();
    let firstResolved = 0;
    const total = orgIds.length;

    const emit = () => {
      const next = Array.from(byId.values()).sort((a, b) => {
        if (a.isYourStudio !== b.isYourStudio) return a.isYourStudio ? -1 : 1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
      setOrgs(next);
    };

    const unsubs = orgIds.map((orgId) =>
      onSnapshot(
        doc(db, 'organizations', orgId),
        (snap) => {
          if (!snap.exists()) {
            byId.delete(orgId);
          } else {
            const data = snap.data() as Record<string, unknown> | undefined;
            const name =
              typeof data?.name === 'string' ? (data.name as string).trim() : '';
            const ownerId =
              typeof data?.ownerId === 'string' ? (data.ownerId as string) : '';
            const logoUrl =
              typeof data?.logoUrl === 'string' && data.logoUrl
                ? (data.logoUrl as string)
                : null;
            // OWNER FIRST: your own studio always labels Super Admin —
            // the org doc's ownerId beats a stale/corrupted claims entry.
            const isYourStudio = !!ownerId && ownerId === uid;
            const role = isYourStudio
              ? 'superAdmin'
              : claimsOrgsRef.current[orgId];
            byId.set(orgId, {
              id: orgId,
              name: name || 'Studio',
              ownerId,
              isYourStudio,
              roleLabel: role ? ROLE_LABELS[role] ?? 'Member' : 'Member',
              logoUrl,
            });
          }
          emit();
          firstResolved += 1;
          if (firstResolved >= total) setLoading(false);
        },
        (err) => {
          // One bad org doc shouldn't blank the whole list. Common
          // cause: rules denial when reading an org the user is a
          // client of and the client-read rule hasn't been deployed
          // yet. Log and keep going.
          console.warn(
            `[useMyOrganizations] snapshot error for ${orgId}:`,
            err,
          );
          firstResolved += 1;
          if (firstResolved >= total) setLoading(false);
          setError(err instanceof Error ? err : new Error(String(err)));
        },
      ),
    );

    return () => {
      unsubs.forEach((u) => u());
    };
    // Only re-fire when the SET of org-ids changes — claims object
    // identity changes don't tear down listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, claimsLoading, orgIdsKey]);

  return { orgs, loading, error };
}
